import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarDays,
  CircleDollarSign,
  CircleGauge,
  ClockAlert,
  HandCoins,
  ShieldAlert,
  Users,
  WalletCards,
} from "lucide-react";
import type { EntityRecord } from "@wedding/schemas";
import { Card, Skeleton } from "@wedding/ui";
import { api } from "../api";

type DashboardData = {
  projectName: string;
  weddingDate: string;
  today: string;
  focusSummary: string;
  kpis: {
    daysToWedding: number;
    taskCompletionRate: number;
    totalBudgetCents: number;
    actualCents: number;
    paidCents: number;
    outstandingCents: number;
    overdueTasks: number;
    pendingGuests: number;
    attendingGuests: number;
    highRisks: number;
  };
  charts: {
    taskStatus: Array<{ name: string; value: number }>;
    rsvp: Array<{ name: string; value: number }>;
    budgetByCategory: Array<{ name: string; value: number }>;
  };
  reminders: {
    dueSoon: EntityRecord[];
    unpaid: EntityRecord[];
    seatingIssues: string[];
  };
  generatedAt: string;
  dataVersion: number;
  isStale: boolean;
};

const money = (value: number) =>
  new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);

const palette = ["#b69a64", "#87977a", "#d59b8e", "#66788a", "#8b6f7f"];

function weddingDateLabel(date: string) {
  return date.replaceAll("-", ".");
}

function weddingDateEyebrow(date: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 6) return "夜深了";
  if (hour < 12) return "早上好";
  if (hour < 18) return "下午好";
  return "晚上好";
}

export function Dashboard() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const navigate = useNavigate();
  const dashboard = useQuery({
    queryKey: [workspaceId, "dashboard"],
    queryFn: () =>
      api.dashboard(workspaceId).then((result) => result.data as DashboardData),
    refetchInterval: 60_000,
  });

  if (dashboard.isLoading)
    return (
      <div className="page">
        <Skeleton lines={12} />
      </div>
    );
  if (!dashboard.data)
    return <div className="error-state">婚礼总览暂时无法加载</div>;

  const { kpis, charts, reminders, weddingDate, focusSummary } = dashboard.data;
  const kpiCards = [
    {
      label: "距离婚礼",
      value: `${kpis.daysToWedding} 天`,
      hint: weddingDateLabel(weddingDate),
      icon: CalendarDays,
      path: "timeline",
      search: {},
    },
    {
      label: "任务完成率",
      value: `${kpis.taskCompletionRate}%`,
      hint: `${kpis.overdueTasks} 项已逾期`,
      icon: CircleGauge,
      path: "tasks",
      search: {},
    },
    {
      label: "实际总花费",
      value: money(kpis.actualCents),
      hint: `预算 ${money(kpis.totalBudgetCents)}`,
      icon: WalletCards,
      path: "budget",
      search: {},
    },
    {
      label: "待付款",
      value: money(kpis.outstandingCents),
      hint: `已付 ${money(kpis.paidCents)}`,
      icon: HandCoins,
      path: "budget",
      search: { view: "unpaid" },
    },
    {
      label: "确认出席",
      value: `${kpis.attendingGuests} 人`,
      hint: `${kpis.pendingGuests} 人待确认`,
      icon: Users,
      path: "guests",
      search: { attendance: "待确认" },
    },
    {
      label: "高风险事项",
      value: `${kpis.highRisks} 项`,
      hint: "需要持续跟进",
      icon: ShieldAlert,
      path: "risks",
      search: { riskLevel: "高" },
    },
  ];

  return (
    <div className="page dashboard">
      <header className="dashboard-hero">
        <div>
          <span className="eyebrow">{weddingDateEyebrow(weddingDate)}</span>
          <h1>
            {greeting()}，{dashboard.data.projectName}筹备正稳步向前
          </h1>
          <p>{focusSummary}</p>
        </div>
        <div className="date-seal">
          <strong>{kpis.daysToWedding}</strong>
          <span>{kpis.daysToWedding === 0 ? "就是今天" : "天后举行"}</span>
        </div>
      </header>

      <div className="kpi-grid">
        {kpiCards.map(({ label, value, hint, icon: Icon, path, search }) => (
          <Link
            key={label}
            className="kpi-link"
            to="/w/$workspaceId/$section"
            params={{ workspaceId, section: path }}
            search={search}
            aria-label={`查看${label}详情`}
          >
            <Card>
              <header>
                <span>{label}</span>
                <Icon size={18} />
              </header>
              <strong className="kpi-value">{value}</strong>
              <small>{hint}</small>
            </Card>
          </Link>
        ))}
      </div>

      <div className="dashboard-grid">
        <Card className="chart-wide">
          <header className="card-title">
            <div>
              <span className="eyebrow">筹备进度</span>
              <h2>任务状态分布</h2>
            </div>
            <span className="fresh">实时聚合</span>
          </header>
          <div className="chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts.taskStatus}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar
                  dataKey="value"
                  radius={[5, 5, 0, 0]}
                  fill="#b69a64"
                  cursor="pointer"
                  onClick={(entry) => {
                    const status = String(entry.name ?? "");
                    if (!status) return;
                    void navigate({
                      to: "/w/$workspaceId/$section",
                      params: { workspaceId, section: "tasks" },
                      search: { status },
                    });
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="status-filter-buttons" aria-label="按任务状态查看">
            {charts.taskStatus.map((item) => (
              <button
                type="button"
                key={item.name}
                onClick={() =>
                  void navigate({
                    to: "/w/$workspaceId/$section",
                    params: { workspaceId, section: "tasks" },
                    search: { status: item.name },
                  })
                }
                aria-label={`查看${item.name}任务`}
              >
                <span>{item.name}</span>
                <strong>{item.value}</strong>
              </button>
            ))}
          </div>
          <p className="chart-summary">
            文字摘要：当前总完成率 {kpis.taskCompletionRate}%，另有{" "}
            {kpis.overdueTasks} 项逾期任务需要处理。
          </p>
        </Card>

        <Card>
          <header className="card-title">
            <div>
              <span className="eyebrow">宾客回执</span>
              <h2>宾客回复情况</h2>
            </div>
          </header>
          <div className="chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={charts.rsvp}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={82}
                >
                  {charts.rsvp.map((item, index) => (
                    <Cell
                      key={item.name}
                      fill={palette[index % palette.length] ?? "#b69a64"}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="legend-row">
            {charts.rsvp.map((item, index) => (
              <span key={item.name}>
                <i
                  style={{
                    background: palette[index % palette.length] ?? "#b69a64",
                  }}
                />
                {item.name} {item.value}
              </span>
            ))}
          </div>
          <p className="chart-summary">
            文字摘要：已确认出席 {kpis.attendingGuests} 人，仍有{" "}
            {kpis.pendingGuests} 人待回复。
          </p>
        </Card>

        <Card className="reminders">
          <header className="card-title">
            <div>
              <span className="eyebrow">近期重点</span>
              <h2>重点提醒</h2>
            </div>
            <ClockAlert size={19} />
          </header>
          <ul>
            {reminders.dueSoon.slice(0, 5).map((item) => (
              <li key={item.id}>
                <Link
                  className="reminder-record-link"
                  to="/w/$workspaceId/$section"
                  params={{ workspaceId, section: "tasks" }}
                  search={{ record: item.id }}
                  aria-label={`查看任务：${String(item.data.title)}`}
                >
                  <span
                    className={`dot ${String(item.data.dueDate) < dashboard.data.today ? "danger" : "warning"}`}
                  />
                  <div>
                    <strong>{String(item.data.title)}</strong>
                    <small>
                      {String(item.data.owner || "待分配")} ·{" "}
                      {String(item.data.dueDate || "日期待定")}
                    </small>
                  </div>
                </Link>
              </li>
            ))}
            {!reminders.dueSoon.length && <li>当前暂无到期任务</li>}
          </ul>
          <div className="reminder-stats">
            <Link
              to="/w/$workspaceId/$section"
              params={{ workspaceId, section: "budget" }}
              search={{ view: "unpaid" }}
            >
              <CircleDollarSign size={17} />
              {reminders.unpaid.length} 笔待付款
            </Link>
            <Link
              to="/w/$workspaceId/$section"
              params={{ workspaceId, section: "seating" }}
            >
              <ShieldAlert size={17} />
              {reminders.seatingIssues.length} 个排座问题
            </Link>
          </div>
        </Card>

        <Card className="chart-wide">
          <header className="card-title">
            <div>
              <span className="eyebrow">预算支出</span>
              <h2>各类别实际支出</h2>
            </div>
          </header>
          <div className="chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts.budgetByCategory.slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" hide />
                <YAxis
                  tickFormatter={(value) => `¥${Math.round(value / 100)}`}
                />
                <Tooltip formatter={(value) => money(Number(value))} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#87977a"
                  fill="#87977a33"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="chart-summary">
            文字摘要：实际支出共 {money(kpis.actualCents)}，待付款{" "}
            {money(kpis.outstandingCents)}。
          </p>
        </Card>
      </div>

      <footer className="snapshot">
        数据生成于{" "}
        {new Date(dashboard.data.generatedAt).toLocaleString("zh-CN")} · 版本{" "}
        {dashboard.data.dataVersion} ·{" "}
        {dashboard.data.isStale ? "缓存数据" : "最新数据"}
      </footer>
    </div>
  );
}
